import { AtSign } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'
import { PlatformIcon } from '@/components/brand-mentions/PlatformIcon'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import {
	PLATFORM_LABELS,
	type BrandMention,
	type BrandMentionJob,
	type BrandMentionPlatform,
} from '@/types/brandMentions'
import { formatCompactTime, mentionPreview } from '@/components/brand-mentions/mentionUtils'

export function MentionList({
	mentions,
	loading,
	selectedId,
	onSelect,
	availablePlatforms,
	platforms,
	onTogglePlatform,
	onResetFilters,
	jobs,
	emptyMessage,
	nextSearchMessage,
	canToggleEnabled,
	togglingEnabled,
	onEnabledChange,
}: {
	mentions: BrandMention[]
	loading: boolean
	selectedId: string | null
	onSelect: (id: string) => void
	availablePlatforms: readonly BrandMentionPlatform[]
	platforms: BrandMentionPlatform[]
	onTogglePlatform: (platform: BrandMentionPlatform) => void
	onResetFilters: () => void
	jobs: BrandMentionJob[]
	emptyMessage: string
	nextSearchMessage?: string
	canToggleEnabled?: boolean
	togglingEnabled?: boolean
	onEnabledChange?: (enabled: boolean) => void
}) {
	const { t } = useTranslation()

	return (
		<div className="flex h-full flex-col bg-background border-r border-border/60">
			<div className="border-b border-border/60 bg-card/30 px-4 py-4">
				<div className="mb-3 flex items-center justify-between gap-3">
					<div className="flex min-w-0 items-center gap-2">
						<AtSign className="h-5 w-5 shrink-0 text-primary" />
						<h1 className="truncate text-xl font-semibold tracking-tight md:text-2xl">
							{t('brandMentions.title')}
						</h1>
					</div>
					{canToggleEnabled && onEnabledChange && (
						<Switch
							checked
							disabled={togglingEnabled}
							onCheckedChange={onEnabledChange}
							aria-label={t('brandMentions.enableLabel')}
						/>
					)}
				</div>
				{nextSearchMessage && (
					<p className="mb-3 text-sm text-muted-foreground">
						{nextSearchMessage}
					</p>
				)}
				<div className="flex flex-wrap gap-2">
					{availablePlatforms.map((platform) => {
						const active = platforms.includes(platform)
						return (
							<button
								key={platform}
								type="button"
								aria-pressed={active}
								onClick={() => onTogglePlatform(platform)}
								className={cn(
									'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors',
									active
										? 'bg-primary text-primary-foreground'
										: 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
								)}>
								<PlatformIcon platform={platform} className="h-3.5 w-3.5" />
								{PLATFORM_LABELS[platform]}
							</button>
						)
					})}
				</div>
				{platforms.length > 0 && (
					<button
						type="button"
						className="mt-2 text-sm text-primary"
						onClick={onResetFilters}>
						{t('brandMentions.resetFilters')}
					</button>
				)}
				{jobs.some((job) => job.error) && (
					<div className="mt-3 flex flex-wrap gap-1.5">
						{availablePlatforms.map((platform) => {
							const platformJobs = jobs.filter((job) => job.platform === platform)
							if (!platformJobs.length) return null
							const failed = platformJobs.some((job) => job.error)
							const done = platformJobs.every((job) => job.done)
							return (
								<span
									key={platform}
									className={cn(
										'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]',
										failed
											? 'bg-amber-50 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200'
											: done
												? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200'
												: 'bg-muted text-muted-foreground'
									)}>
									<PlatformIcon platform={platform} className="h-3 w-3" colored />
									{PLATFORM_LABELS[platform]}
								</span>
							)
						})}
					</div>
				)}
			</div>
			<div className="flex-1 overflow-y-auto overflow-x-hidden">
				{loading ? (
					<div className="space-y-2 px-2 py-2">
						{[0, 1, 2, 3].map((index) => (
							<div key={index} className="rounded-xl border border-border p-4">
								<div className="flex gap-3">
									<Skeleton className="h-11 w-11 shrink-0 rounded-full" />
									<div className="min-w-0 flex-1 space-y-2">
										<div className="flex justify-between">
											<Skeleton className="h-4 w-24" />
											<Skeleton className="h-3 w-10" />
										</div>
										<Skeleton className="h-3 w-full" />
									</div>
								</div>
							</div>
						))}
					</div>
				) : mentions.length === 0 ? (
					<div className="px-4 py-10 text-center text-sm text-muted-foreground">
						{emptyMessage}
					</div>
				) : (
					<div className="space-y-2 px-2 py-2">
						{mentions.map((mention) => {
							const isSelected = selectedId === mention.id
							const preview = mentionPreview(mention)
							return (
								<button
									key={mention.id}
									type="button"
									onClick={() => onSelect(mention.id)}
									aria-current={isSelected ? 'true' : 'false'}
									className={cn(
										'group w-full cursor-pointer rounded-xl border p-4 text-left transition-all duration-200',
										'bg-background border-border',
										'hover:-translate-y-0.5 hover:border-primary/40 hover:bg-primary/5 hover:shadow-md',
										'active:scale-[0.99] active:shadow-none',
										'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
										isSelected &&
											'bg-primary/10 border-primary/50 shadow-sm ring-1 ring-primary/30'
									)}>
									<div className="flex items-start gap-3">
										<div
											className={cn(
												'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted ring-2 ring-transparent',
												isSelected && 'ring-primary/30'
											)}>
											<PlatformIcon
												platform={mention.platform}
												className="h-5 w-5"
												colored
											/>
										</div>
										<div className="min-w-0 flex-1">
											<div className="mb-1 flex items-center justify-between gap-2">
												<span className="truncate text-sm font-medium text-foreground">
													{mention.author || PLATFORM_LABELS[mention.platform]}
												</span>
												<time
													className="shrink-0 text-xs font-medium text-muted-foreground"
													dateTime={mention.publishedAt}>
													{formatCompactTime(mention.publishedAt, t)}
												</time>
											</div>
											<p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
												{preview || t('brandMentions.emptyPreview')}
											</p>
										</div>
									</div>
								</button>
							)
						})}
					</div>
				)}
			</div>
		</div>
	)
}
